// @ts-nocheck
import { getEventParticipationStatus } from 'shared/api/services/EventsService';

export const ACTIVE_EVENT_ACCESS_UPDATED_EVENT = 'app-access-updated';

type CachedEventAdminPanelAccess = {
  eventId: string;
  canAccessAdminPanel: boolean;
};

let cachedEventAdminPanelAccess: CachedEventAdminPanelAccess | null = null;

const readStoredJson = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const extractSnapshot = (payload) => {
  if (!payload) return null;
  const direct = payload?.data ?? payload?.item ?? payload?.event ?? payload?.result ?? payload;
  if (!direct || typeof direct !== 'object') return direct;
  const nested = direct?.data ?? direct?.item ?? direct?.event ?? direct?.result;
  return nested && typeof nested === 'object' ? nested : direct;
};

export const getActiveEventStorage = () => readStoredJson('activeEvent');

export const getActiveEventIdFromStorage = () => {
  const activeEvent = getActiveEventStorage();
  return activeEvent?.activeEventId || activeEvent?.eventId || activeEvent?.id || activeEvent?.event?.id || '';
};

export const getCachedEventAdminPanelAccess = () => cachedEventAdminPanelAccess;

export const canCurrentUserAccessEventAdminPanel = () => Boolean(cachedEventAdminPanelAccess?.canAccessAdminPanel);

export const setCachedEventAdminPanelAccess = (next: CachedEventAdminPanelAccess | null) => {
  const normalized = next
    ? {
        eventId: String(next.eventId || '').trim(),
        canAccessAdminPanel: Boolean(next.canAccessAdminPanel),
      }
    : null;

  const current = cachedEventAdminPanelAccess;
  const isSame =
    current?.eventId === normalized?.eventId &&
    current?.canAccessAdminPanel === normalized?.canAccessAdminPanel;

  if (isSame) {
    return current;
  }

  cachedEventAdminPanelAccess = normalized;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(ACTIVE_EVENT_ACCESS_UPDATED_EVENT));
  }
  return cachedEventAdminPanelAccess;
};

export const isCurrentUserEventAdminSession = () => {
  try {
    return canCurrentUserAccessEventAdminPanel();
  } catch {
    return false;
  }
};

export const readStoredEventVisibility = () => {
  const activeEvent = getActiveEventStorage();
  const directParticipants = activeEvent?.canSeeParticipants;
  const directVisitors = activeEvent?.canSeeVisitors;
  const nestedParticipants = activeEvent?.event?.canSeeParticipants;
  const nestedVisitors = activeEvent?.event?.canSeeVisitors;
  const directViewerRoles = Array.isArray(activeEvent?.viewerRoles) ? activeEvent.viewerRoles : [];
  const nestedViewerRoles = Array.isArray(activeEvent?.event?.viewerRoles) ? activeEvent.event.viewerRoles : [];

  const hasParticipantsSnapshot = typeof directParticipants === 'boolean' || typeof nestedParticipants === 'boolean';
  const hasVisitorsSnapshot = typeof directVisitors === 'boolean' || typeof nestedVisitors === 'boolean';

  return {
    canSeeParticipants: Boolean(directParticipants ?? nestedParticipants),
    canSeeVisitors: Boolean(directVisitors ?? nestedVisitors),
    hasSnapshot: hasParticipantsSnapshot || hasVisitorsSnapshot,
    viewerRoles: [...directViewerRoles, ...nestedViewerRoles],
  };
};

export const normalizeEventVisibilitySnapshot = (snapshot) => ({
  canSeeParticipants: Boolean(snapshot?.canSeeParticipants),
  canSeeVisitors: Boolean(snapshot?.canSeeVisitors),
  status: snapshot?.status || 'none',
  canAccessAdminPanel: Boolean(snapshot?.canAccessAdminPanel),
  viewerRoles: Array.isArray(snapshot?.viewerRoles) ? snapshot.viewerRoles : [],
});

const serializeSnapshot = (value) => {
  try {
    return JSON.stringify(value ?? null);
  } catch {
    return '';
  }
};

export const fetchEventVisibilitySnapshot = async (eventId) => {
  const response = await getEventParticipationStatus(eventId);
  const snapshot = extractSnapshot(response?.data) || {};
  return normalizeEventVisibilitySnapshot(snapshot);
};

export const saveActiveEventSnapshot = (patch) => {
  try {
    const stored = getActiveEventStorage() || {};
    const next = {
      ...stored,
      ...patch,
      event: {
        ...(stored.event || {}),
        ...(patch?.event || {}),
      },
    };
    if (serializeSnapshot(stored) === serializeSnapshot(next)) {
      return stored;
    }
    window.localStorage.setItem('activeEvent', JSON.stringify(next));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(ACTIVE_EVENT_ACCESS_UPDATED_EVENT));
    }
    return next;
  } catch {
    return null;
  }
};
